import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { selectColumnsExistInDbData } from '../../duckDB/duckDBSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import {
  selectCurrMeasure,
  selectCurrPointId,
  selectHoverPointId,
} from '../../select/selectSlice';
import {
  ScanData,
  ScanDataEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataDefinitions';
import { selectShowValidPointsDropdown } from '../scanDataSlice';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

export enum ScanDataFilterEnum {
  // class_name = ScanDataEnum.class_name,  // TODO: ADD THESE FILTERS !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! I think they were removed when useScanDataQuery was refactored bc they are filtered in there now, but if the disableClassFilter is set to true, then they are not filtered in there anymore so need to optionally add them back here (this is useful for debug info to see the scan data for all features for a given point instead of just the currently selected feature)
  // model_name = ScanDataEnum.model_name,
  // model_version = ScanDataEnum.model_version,
  point_id = ScanDataEnum.point_id,
}

export interface FilterScanDataOptions {
  activeFilters?: ScanDataFilterEnum[];
  isMapHoverData?: boolean;
  skip?: boolean;
  disableClassFilter?: boolean;
  customPointId?: string;
}

/**
 * Custom React hook to filter ScanData based on the provided options
 *
 * @param options - FilterScanDataOptions object with tableName and an optional list of activeFilters
 * @returns - Filtered ScanData or null
 */
const useFilterScanData = ({
  activeFilters,
  isMapHoverData = false,
  skip = false,
  disableClassFilter = false,
  customPointId = '',
}: FilterScanDataOptions): DuckQueryReturnListType<ScanData> => {
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currScanDataMeasure =
    convertMeasureCubeRanchToScanDataBlock(currMeasure);
  const currPointId = useAppSelector(selectCurrPointId);
  const currHoverPointId = useAppSelector(selectHoverPointId);
  const showValidPointsDropdown = useAppSelector(selectShowValidPointsDropdown);
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);
  const pointId = customPointId
    ? customPointId
    : isMapHoverData
    ? currHoverPointId
    : currPointId;

  const skipCondition = useMemo(
    () =>
      skip ||
      !currScanDataMeasure ||
      !columnsExistInDbData ||
      columnsExistInDbData.isLoading ||
      !!columnsExistInDbData.error ||
      !columnsExistInDbData.data ||
      (activeFilters?.includes(ScanDataFilterEnum.point_id) && !pointId),
    [skip, currScanDataMeasure, columnsExistInDbData, activeFilters, pointId]
  );

  const [cols, filters] = useMemo(() => {
    if (
      !columnsExistInDbData ||
      columnsExistInDbData.isLoading ||
      !!columnsExistInDbData.error
    )
      return [[], []];

    // filter out columns from that not exists in older scan data dumps
    const cols = (
      columnsExistInDbData.data
        ? Object.entries(columnsExistInDbData.data)
            .filter(([, exists]) => exists)
            .map(([col]) => col)
        : []
    ) as string[];
    const filters = [];

    if (activeFilters?.includes(ScanDataFilterEnum.point_id)) {
      filters.push({
        col: ScanDataEnum.point_id,
        op: OperatorEnum.eq,
        value: pointId,
      });
    }

    // TODO: add filter for class_name, model_name, model_version !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

    if (currScanDataMeasure && showValidPointsDropdown === 'showValidPoints') {
      // Typescript cannot resolve currScanDataMeasure as a string so let's help him.
      filters.push({
        col: currScanDataMeasure.toString(),
        op: OperatorEnum.neq,
        value: null,
      });
    }

    if (
      currScanDataMeasure &&
      showValidPointsDropdown === 'showInvalidPoints'
    ) {
      filters.push({
        col: currScanDataMeasure.toString(),
        op: OperatorEnum.eq,
        value: null,
      });
    }

    return [cols, filters];
  }, [
    activeFilters,
    currScanDataMeasure,
    columnsExistInDbData,
    showValidPointsDropdown,
  ]);

  // Get the scan data from duckDB filtered by the current feature
  const scanDataFilteredByFeature = useScanDataQuery<ScanData>({
    cols,
    filters,
    skip: skipCondition,
    useUnitsTransformSubstringMatching: true,
    disableClassFilter: disableClassFilter,
  });

  return scanDataFilteredByFeature;
};

export default useFilterScanData;
